import { Autocomplete, TextField } from '@mui/material';
import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import SearchFilled from '../../assets/search-filled.svg';
import { EDIT_AD_GROUP_URL, EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import { getRequestJSON } from '../../util/services';

interface JumpToProps {
  jumpType: string;
}

function JumpTo({ jumpType = 'lineItem' }: JumpToProps) {
  const [inputValue, setInputValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const { apiHost } = useConfig();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();

  const determineAPIEndpoint = (): string => {
    if (jumpType === 'lineItem') {
      return `${apiHost}/admin/insertion_orders/`;
    }
    // ad group
    return `${apiHost}/admin/campaigns/`;
  };

  const callAPI = async () => {
    // Snapshot the searched id so the error message and navigation stay stable
    // even after we clear the field on success below.
    const id = inputValue;
    const apiEndpoint = determineAPIEndpoint();
    // check for line item or ad group record
    const response = await getRequestJSON(apiEndpoint + id);
    // return if no record is found
    if (response?.code === 404) {
      showSnackBar(`No record found with id ${id}`, 'error');
      setHasError(true);
      return;
    }
    // Guard against a malformed/empty response: without a contract to jump to
    // there's nothing to navigate to, so bail before touching the router or field.
    if (!response?.contract_id) {
      return;
    }
    // navigate to found record
    if (jumpType === 'lineItem') {
      navigate(generatePath(EDIT_LINE_ITEM_URL, { id: response.contract_id, lineItemId: id.toString() }));
    } else {
      navigate(generatePath(EDIT_AD_GROUP_URL, { id: response.contract_id, adGroupId: id.toString() }));
    }
    // The header search persists across routes, so reset the field after jumping.
    setInputValue('');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasError(false);
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      callAPI();
    }
  };

  return (
    <Autocomplete
      freeSolo
      value={inputValue}
      options={[]}
      size="small"
      style={{ width: 160 }}
      renderInput={(params) => (
        <TextField
          sx={{ backgroundColor: 'white' }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          variant="outlined"
          size="small"
          placeholder={jumpType === 'lineItem' ? 'Line Item ID' : 'Ad Group ID'}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onBlur={() => {
            if (hasError) {
              setInputValue('');
              setHasError(false);
            }
          }}
          error={hasError}
          InputProps={{
            ...params.InputProps,
            startAdornment: <img src={SearchFilled} alt="Search" />,
          }}
        />
      )}
    />
  );
}

export default JumpTo;
